import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Screwdriver,
  ShoppingCart,
  Phone,
  Wrench,
  Notebook,
  CurrencyEur,
  Package,
  DotsThreeVertical,
  Check,
  Pencil,
  Trash,
  Printer,
  Tag,
  ArrowsClockwise,
  Minus,
  PushPin,
  Calendar,
  EnvelopeSimple,
  Storefront,
  UserCircle,
  ListChecks,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { daysSince, TYPE_BY_VALUE } from "./constants";

const ICON_MAP = {
  screwdriver: Screwdriver,
  cart: ShoppingCart,
  phone: Phone,
  wrench: Wrench,
  notebook: Notebook,
  euro: CurrencyEur,
  package: Package,
};

const CHANNEL_ICON_MAP = {
  tel: Phone,
  mail: EnvelopeSimple,
  store: Storefront,
  client: UserCircle,
};

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

function formatDateOnly(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function isoToDateInput(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export default function StickyNote({
  note,
  onDragStart,
  onDragEnd,
  onEdit,
  onDelete,
  onComplete,
  onUncomplete,
  onCharged,
  onChangeCategory,
  onToggleRead,
  onSetInProgress,
  onMinimize,
  onPrint,
  onPrintLabel,
  onUpdateField,
  onPartialExecute,
  onOpenContract,
  isMobile,
  types,
  channels,
  people,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [inProgressPickerOpen, setInProgressPickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const meta = TYPE_BY_VALUE[note.type] || types[0];
  const TypeIcon = ICON_MAP[meta.icon] || PushPin;

  const days = daysSince(note.created_at);
  const isNew = days === 0;

  const hasItems =
    note.type === "order" &&
    Array.isArray(note?.data?.items) &&
    note.data.items.length > 0;
  const canEnablePartial = note.type === "order" && note.in_progress && hasItems;

  // Fresh-note blink for the first 60 seconds after key events
  // - new post creation
  // - moving into ΠΡΟΣ ΧΡΕΩΣΗ (needs_payment_at)
  // - moving into ΕΚΤΕΛΕΣΜΕΝΑ (completed_at)
  const [isFresh, setIsFresh] = useState(false);
  // Short "blink" effect when the note gets locally updated (any change).
  const [isBlinking, setIsBlinking] = useState(false);
  useEffect(() => {
    if (!note._blink_at) return;
    setIsBlinking(true);
    const t = setTimeout(() => setIsBlinking(false), 1400);
    return () => clearTimeout(t);
  }, [note._blink_at]);

  useEffect(() => {
    const ts = [note.created_at, note.needs_payment_at, note.completed_at]
      .filter(Boolean)
      .map((t) => new Date(t).getTime())
      .filter((n) => !Number.isNaN(n));
    if (ts.length === 0) {
      setIsFresh(false);
      return;
    }
    const newest = Math.max(...ts);
    const remaining = 60_000 - (Date.now() - newest);
    if (remaining <= 0) {
      setIsFresh(false);
      return;
    }
    setIsFresh(true);
    const t = setTimeout(() => setIsFresh(false), remaining);
    return () => clearTimeout(t);
  }, [note.created_at, note.needs_payment_at, note.completed_at]);

  const stopPointer = (e) => e.stopPropagation();

  return (
    <motion.div
      drag={!isMobile}
      dragSnapToOrigin
      dragMomentum={false}
      onDragStart={() => {
        if (isMobile) return;
        setIsDragging(true);
        onDragStart && onDragStart();
      }}
      onDragEnd={(_e, info) => {
        if (isMobile) return;
        setIsDragging(false);
        onDragEnd && onDragEnd(info.point.x, info.point.y);
      }}
      whileHover={!isDragging && !isMobile ? { y: -2 } : {}}
      whileDrag={!isMobile ? { scale: 1.03, zIndex: 50, rotate: 0 } : {}}
      className={`relative w-full select-none note-paper rounded-sm ${
        isMobile ? "" : "cursor-grab active:cursor-grabbing"
      } ${note.completed ? "opacity-75" : ""} ${
        isFresh && !isDragging ? "note-fresh" : ""
      } ${isBlinking && !isDragging ? "note-blink" : ""}`}
      style={{
        backgroundColor: note.color || "#fef9c3",
        "--note-accent": note.color || "#fef9c3",
        boxShadow: isDragging
          ? "10px 18px 48px rgba(0,0,0,0.22)"
          : isFresh
          ? undefined
          : "2px 4px 14px rgba(0,0,0,0.10), 0 1px 0 rgba(255,255,255,0.4) inset",
      }}
      data-testid={`note-card-${note.id}`}
    >
      <span className="note-tape" aria-hidden />

      {/* DIAGONAL STAMP — visible when in_progress. If ΣΕ ΑΝΑΜΟΝΗ is also
          active, this stamp is shifted up so both can coexist. */}
      {note.in_progress && !note.completed && (
        <div
          className={`absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden rounded-sm ${
            note.read ? "-translate-y-5" : ""
          }`}
        >
          <div
            className="-rotate-[18deg] border-4 border-red-700/45 text-red-700/55 font-black text-xl tracking-widest px-5 py-1.5 rounded-md text-center whitespace-nowrap"
            style={{
              boxShadow: "inset 0 0 0 2px rgba(185,28,28,0.3)",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
          >
            ΣΕ ΕΞΕΛΙΞΗ
            {note.in_progress_by ? (
              <div className="text-[10px] tracking-[0.3em] mt-0.5 text-red-700/65 font-bold">
                {note.in_progress_by}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* DIAGONAL STAMP — ΣΕ ΑΝΑΜΟΝΗ (orange). Can coexist with ΣΕ ΕΞΕΛΙΞΗ;
          when both are on, this one is shifted down. */}
      {note.read && !note.completed && (
        <div
          className={`absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden rounded-sm ${
            note.in_progress ? "translate-y-5" : ""
          }`}
        >
          <div
            className="-rotate-[18deg] border-4 border-orange-600/55 text-orange-700/70 font-black text-xl tracking-widest px-5 py-1.5 rounded-md text-center whitespace-nowrap"
            style={{
              boxShadow: "inset 0 0 0 2px rgba(234,88,12,0.35)",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
          >
            ΣΕ ΑΝΑΜΟΝΗ
          </div>
        </div>
      )}

      {/* COMPLETED STAMP */}
      {note.completed && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden rounded-sm">
          <div className="-rotate-[14deg] border-4 border-green-700/50 text-green-700/60 font-black text-xl tracking-widest px-5 py-1.5 rounded-md">
            ΕΚΤΕΛΕΣΤΗΚΕ
            {note.executed_by ? (
              <div className="text-[10px] tracking-[0.3em] mt-0.5 text-green-700/70 font-bold">
                {note.executed_by}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* PAYMENT FLAG (€) corner ribbon */}
      {note.needs_payment && (
        <div
          className="absolute -top-2 -right-2 z-10 w-9 h-9 rounded-full bg-amber-400 border-2 border-ink shadow-md flex items-center justify-center"
          title="ΕΚΚΡΕΜΕΙ ΧΡΕΩΣΗ"
          data-testid={`note-needs-payment-${note.id}`}
        >
          <CurrencyEur size={18} weight="bold" className="text-ink" />
        </div>
      )}

      <div className="relative p-3 pt-4 flex flex-col gap-1.5" data-testid={`note-content-${note.id}`}>
        {/* TOP ROW: icon + badges + menu */}
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-1 flex-nowrap min-w-0">
            <div
              className="w-7 h-7 rounded-full bg-black/85 text-white flex items-center justify-center shrink-0"
              title={meta.label}
            >
              <TypeIcon size={14} weight="bold" />
            </div>
            {/* NEW or days badge */}
            {isNew ? (
              <span
                className="text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded bg-red-600 text-white"
                data-testid={`note-new-${note.id}`}
              >
                NEW
              </span>
            ) : (
              <span
                className="text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded border-2 border-red-600 text-red-600 bg-white/40"
                data-testid={`note-days-${note.id}`}
              >
                {days}d
              </span>
            )}
            {/* ΑΝΑΜΟΝΗ toggle (repurposed read field) */}
            <button
              onPointerDown={stopPointer}
              onClick={() => onToggleRead(note)}
              data-testid={`note-read-${note.id}`}
              title="ΣΕ ΑΝΑΜΟΝΗ"
              className={`text-[9px] font-bold uppercase tracking-tight px-1 py-0.5 rounded border transition-all flex items-center gap-0.5 shrink-0 ${
                note.read
                  ? "bg-orange-500 text-white border-orange-600"
                  : "bg-white/40 border-ink/20 text-ink/55 hover:border-orange-500"
              }`}
            >
              {note.read && <Check size={8} weight="bold" />} ΑΝΑΜΟΝΗ
            </button>
            {/* in_progress check (opens picker) */}
            <Popover
              open={inProgressPickerOpen}
              onOpenChange={setInProgressPickerOpen}
            >
              <PopoverTrigger asChild>
                <button
                  onPointerDown={stopPointer}
                  data-testid={`note-progress-${note.id}`}
                  title="ΑΝΑΘΕΣΗ"
                  className={`text-[9px] font-bold uppercase tracking-tight px-1 py-0.5 rounded border transition-all flex items-center gap-0.5 shrink-0 ${
                    note.in_progress
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white/40 border-ink/20 text-ink/55 hover:border-red-500"
                  }`}
                >
                  {note.in_progress && <Check size={8} weight="bold" />} ΑΝΑΘΕΣΗ
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-44 p-1"
                onPointerDown={stopPointer}
              >
                <div className="text-[10px] uppercase tracking-widest font-bold text-ink/60 px-2 py-1.5">
                  ΑΝΑΘΕΣΗ ΣΕ
                </div>
                {people.map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      onSetInProgress(note, p);
                      setInProgressPickerOpen(false);
                    }}
                    data-testid={`progress-pick-${note.id}-${p}`}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs font-semibold hover:bg-black/5 ${
                      note.in_progress_by === p ? "bg-black/10" : ""
                    }`}
                  >
                    {p}
                  </button>
                ))}
                {note.in_progress && (
                  <>
                    <div className="h-px bg-black/10 my-1" />
                    <button
                      onClick={() => {
                        onSetInProgress(note, null);
                        setInProgressPickerOpen(false);
                      }}
                      className="w-full text-left px-2 py-1.5 rounded text-xs font-semibold text-red-600 hover:bg-red-50"
                    >
                      ΑΚΥΡΩΣΗ
                    </button>
                  </>
                )}
              </PopoverContent>
            </Popover>
          </div>

          {/* MENU */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onPointerDown={stopPointer}
                className="p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
                data-testid={`note-menu-${note.id}`}
                aria-label="ΜΕΝΟΥ"
              >
                <DotsThreeVertical size={18} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[180px]"
              onPointerDown={stopPointer}
            >
              {note.needs_payment && !note.completed && (
                <>
                  <DropdownMenuItem
                    onClick={() => onCharged(note)}
                    data-testid={`menu-charged-${note.id}`}
                    className="font-bold uppercase tracking-wider text-xs text-amber-700 focus:text-amber-800"
                  >
                    <CurrencyEur size={14} className="mr-2" weight="bold" />
                    ΧΡΕΩΘΗΚΕ
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {note.completed ? (
                <DropdownMenuItem
                  onClick={() => onUncomplete(note)}
                  data-testid={`menu-uncomplete-${note.id}`}
                  className="font-bold uppercase tracking-wider text-xs"
                >
                  <ArrowsClockwise size={14} className="mr-2" />
                  ΕΠΑΝΑΦΟΡΑ
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => onComplete(note)}
                  data-testid={`menu-complete-${note.id}`}
                  disabled={!note.in_progress_by}
                  className={`font-bold uppercase tracking-wider text-xs ${
                    note.in_progress_by
                      ? "text-green-700"
                      : "text-ink/30 cursor-not-allowed"
                  }`}
                  title={
                    !note.in_progress_by
                      ? "ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΘΕΣΗ ΣΕ ΥΠΑΛΛΗΛΟ"
                      : "ΕΚΤΕΛΕΣΤΗΚΕ"
                  }
                >
                  <Check size={14} className="mr-2" />
                  ΕΚΤΕΛΕΣΤΗΚΕ
                  {!note.in_progress_by && (
                    <span className="ml-auto text-[8px] text-ink/40">
                      (ΑΝΑΘΕΣΗ)
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              {canEnablePartial && !note.completed && (
                <DropdownMenuItem
                  onClick={() => onPartialExecute && onPartialExecute(note)}
                  data-testid={`menu-partial-${note.id}`}
                  className="font-bold uppercase tracking-wider text-xs text-amber-700 focus:text-amber-800"
                >
                  <ListChecks size={14} className="mr-2" weight="bold" />
                  ΜΕΡΙΚΗ ΕΚΤΕΛΕΣΗ
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onEdit(note)}
                data-testid={`menu-edit-${note.id}`}
                className="font-bold uppercase tracking-wider text-xs"
              >
                <Pencil size={14} className="mr-2" />
                ΑΛΛΑΓΗ
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className="font-bold uppercase tracking-wider text-xs"
                  data-testid={`menu-move-${note.id}`}
                >
                  <ArrowsClockwise size={14} className="mr-2" />
                  ΜΕΤΑΦΟΡΑ
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {types.map((t) => (
                    <DropdownMenuItem
                      key={t.value}
                      onClick={() => onChangeCategory(note, t.value)}
                      disabled={t.value === note.type}
                      data-testid={`menu-move-${note.id}-${t.value}`}
                      className="text-xs font-bold uppercase tracking-wider"
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: t.color }}
                      />
                      {t.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                onClick={() => onPrint(note)}
                data-testid={`menu-print-${note.id}`}
                className="font-bold uppercase tracking-wider text-xs"
              >
                <Printer size={14} className="mr-2" />
                ΕΚΤΥΠΩΣΗ
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onPrintLabel(note)}
                data-testid={`menu-label-${note.id}`}
                className="font-bold uppercase tracking-wider text-xs"
              >
                <Tag size={14} className="mr-2" />
                ΕΤΙΚΕΤΑ
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onMinimize(note)}
                data-testid={`menu-minimize-${note.id}`}
                className="font-bold uppercase tracking-wider text-xs"
              >
                <Minus size={14} className="mr-2" />
                ΕΛΑΧΙΣΤΟΠΟΙΗΣΗ
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(note.id)}
                data-testid={`menu-delete-${note.id}`}
                className="font-bold uppercase tracking-wider text-xs text-red-600 focus:text-red-700"
              >
                <Trash size={14} className="mr-2" />
                ΔΙΑΓΡΑΦΗ
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* CUSTOMER (big visible) */}
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/55 mt-1 flex items-center gap-1.5 flex-wrap">
          <span>{meta.label}</span>
          {note.seq && (
            <span
              className="font-arial font-black tracking-[0.05em] text-ink/75 bg-white/55 border border-ink/15 rounded px-1 py-px"
              data-testid={`note-seq-${note.id}`}
              title="ΑΡΙΘΜΟΣ POST"
            >
              #{note.seq}
            </span>
          )}
          {note?.data?.contract_id && onOpenContract && (
            <button
              onPointerDown={stopPointer}
              onClick={(e) => {
                e.stopPropagation();
                onOpenContract(note.data.contract_id);
              }}
              data-testid={`note-contract-link-${note.id}`}
              title="ΑΝΟΙΓΜΑ ΣΥΜΒΟΛΑΙΟΥ"
              className="inline-flex items-center gap-0.5 font-arial font-black text-[9px] tracking-[0.05em] text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1 py-px hover:bg-emerald-200 transition-colors"
            >
              <Calendar size={9} weight="bold" />
              ΣΥΜΒΟΛΑΙΟ
            </button>
          )}
          {note?.data?.split_from_seq && (
            <span
              title={`ΥΠΟΛΟΙΠΟ ΠΑΡΑΓΓΕΛΙΑΣ #${note.data.split_from_seq}`}
              data-testid={`note-split-from-${note.id}`}
              className="font-arial font-black text-[9px] tracking-[0.05em] text-amber-900 bg-amber-100 border border-amber-300 rounded px-1 py-px"
            >
              ΥΠΟΛΟΙΠΟ #{note.data.split_from_seq}
            </span>
          )}
        </div>
        <h3
          className="font-arial font-bold text-lg leading-tight text-ink break-words"
          data-testid={`note-customer-${note.id}`}
        >
          {note?.data?.customer || "ΧΩΡΙΣ ΠΕΛΑΤΗ"}
        </h3>

        {/* COMPACT FIELDS */}
        <div className="space-y-0.5 mt-1">
          {note?.data?.code && (
            <FieldRow label="ΚΩΔΙΚΟΣ/ΑΦΜ" value={note.data.code} />
          )}
          {note?.data?.phone && (
            <FieldRow label="ΤΗΛΕΦΩΝΟ" value={note.data.phone} />
          )}
          {note.type === "pickup" && note?.data?.device_type && (
            <FieldRow label="ΤΥΠΟΣ" value={note.data.device_type} />
          )}
          {note?.data?.delivery_date && (
            <div
              className="flex items-center gap-1.5 mt-0.5 px-1.5 py-1 rounded bg-red-100/80 border border-red-300"
              data-testid={`note-delivery-${note.id}`}
            >
              <Calendar size={12} weight="bold" className="text-red-700 shrink-0" />
              <span className="text-[9px] uppercase tracking-widest font-black text-red-700 shrink-0">
                ΠΑΡΑΔΟΣΗ
              </span>
              <span className="font-arial font-black text-sm text-red-700 ml-auto">
                {formatDateOnly(note.data.delivery_date)}
              </span>
            </div>
          )}
        </div>

        {/* ORDER ITEMS — display-only checklist (toggling happens via ΜΕΡΙΚΗ ΕΚΤΕΛΕΣΗ dialog) */}
        {note.type === "order" && Array.isArray(note?.data?.items) && note.data.items.length > 0 ? (
          <div className="mt-1.5">
            <div className="text-[9px] uppercase tracking-widest font-bold text-ink/55 mb-0.5">
              ΕΙΔΗ
            </div>
            <div
              className="bg-white/30 rounded p-1.5 space-y-0.5"
              data-testid={`note-items-${note.id}`}
            >
              {note.data.items.map((it, idx) => {
                const qty = Math.max(1, parseInt(it.qty ?? 1, 10) || 1);
                const delivered = Math.max(
                  0,
                  Math.min(
                    qty,
                    parseInt(it.delivered ?? (it.done ? qty : 0), 10) || 0
                  )
                );
                const remaining = Math.max(0, qty - delivered);
                const isDone = delivered >= qty;
                const isPartial = !isDone && delivered > 0;
                return (
                  <div
                    key={`${idx}-${it.text}`}
                    data-testid={`note-item-${note.id}-${idx}`}
                    className={`w-full flex items-start gap-2 px-1.5 py-1 rounded ${
                      isPartial ? "bg-amber-100/70 border-l-2 border-amber-500" : ""
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 shrink-0 mt-0.5 rounded border-2 flex items-center justify-center ${
                        isDone
                          ? "bg-green-700 border-green-700"
                          : isPartial
                          ? "bg-amber-500 border-amber-500"
                          : "bg-white/70 border-ink/40"
                      }`}
                    >
                      {isDone && (
                        <Check size={9} weight="bold" className="text-white" />
                      )}
                      {isPartial && (
                        <span className="w-1.5 h-1.5 rounded-sm bg-white" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-arial text-sm leading-snug break-words ${
                          isDone
                            ? "line-through text-ink/35"
                            : "text-ink/85 font-medium"
                        }`}
                      >
                        <span className="font-black tabular-nums mr-1">
                          {qty}x
                        </span>
                        {it.text}
                      </div>
                      {isPartial && (
                        <div
                          className="text-[9px] uppercase tracking-wider font-bold mt-0.5 flex flex-wrap gap-1"
                          data-testid={`note-item-${note.id}-${idx}-status`}
                        >
                          <span className="text-green-700">
                            ΠΗΓΑΝ {delivered}
                          </span>
                          <span className="text-amber-700">
                            · ΥΠΟΛΟΙΠΟ {remaining}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* DESCRIPTION — bigger (label changes for pickup) */
          note?.data?.description && (
            <div className="mt-1.5">
              <div className="text-[9px] uppercase tracking-widest font-bold text-ink/55 mb-0.5">
                {note.type === "pickup" ? "ΠΕΡΙΓΡΑΦΗ ΒΛΑΒΗΣ" : null}
              </div>
              <div
                className="text-sm text-ink/85 leading-snug whitespace-pre-wrap break-words bg-white/30 rounded p-2 min-h-[50px] font-arial"
                data-testid={`note-description-${note.id}`}
              >
                {note.data.description}
              </div>
            </div>
          )
        )}

        {/* ORDER-ONLY: free-text notes (additional info) */}
        {note.type === "order" && (note?.data?.notes || "").trim() && (
          <div className="mt-1.5">
            <div className="text-[9px] uppercase tracking-widest font-bold text-ink/55 mb-0.5">
              ΣΗΜΕΙΩΣΕΙΣ
            </div>
            <div
              className="text-sm text-ink/85 leading-snug whitespace-pre-wrap break-words bg-white/30 rounded p-2 font-arial italic"
              data-testid={`note-order-notes-${note.id}`}
            >
              {note.data.notes}
            </div>
          </div>
        )}

        {/* PICKUP-ONLY: equipment checks */}
        {note.type === "pickup" &&
          (note?.data?.power_cable ||
            note?.data?.connection_cables ||
            note?.data?.no_accessories ||
            note?.data?.with_box) && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {note.data.no_accessories && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 rounded bg-red-600 text-white flex items-center gap-1"
                  data-testid={`note-no-accessories-${note.id}`}
                >
                  <Check size={9} weight="bold" />
                  ΧΩΡΙΣ ΠΑΡΕΛΚ.
                </span>
              )}
              {note.data.power_cable && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 rounded bg-ink text-white flex items-center gap-1"
                  data-testid={`note-power-cable-${note.id}`}
                >
                  <Check size={9} weight="bold" />
                  ΚΑΛ. ΡΕΥΜΑΤΟΣ
                </span>
              )}
              {note.data.connection_cables && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 rounded bg-ink text-white flex items-center gap-1"
                  data-testid={`note-connection-cables-${note.id}`}
                >
                  <Check size={9} weight="bold" />
                  ΚΑΛ. ΣΥΝΔΕΣΗΣ
                </span>
              )}
              {note.data.with_box && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 rounded bg-ink text-white flex items-center gap-1"
                  data-testid={`note-with-box-${note.id}`}
                >
                  <Check size={9} weight="bold" />
                  ΜΕ ΚΟΥΤΙ
                </span>
              )}
            </div>
          )}

        {/* CHARGE */}
        {note?.data?.charge && (
          <div className="flex items-center justify-between mt-1.5 bg-black/5 rounded px-2 py-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-ink/60">
              ΧΡΕΩΣΗ
            </span>
            <span className="font-arial font-bold text-sm text-ink" data-testid={`note-charge-${note.id}`}>
              {note.data.charge}
              {!String(note.data.charge).includes("€") ? " €" : ""}
            </span>
          </div>
        )}

        {/* BOTTOM ROW: channels (left) + date+person (right) */}
        <div className="flex items-end justify-between gap-2 mt-1.5 pt-1 border-t border-black/10">
          {/* Channels (display only) */}
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            {note.channels &&
              channels
                .filter((ch) => note.channels?.includes(ch.value))
                .map((ch) => {
                  const Icon = CHANNEL_ICON_MAP[ch.value];
                  return (
                    <span
                      key={ch.value}
                      title={ch.label}
                      data-testid={`note-channel-${note.id}-${ch.value}`}
                      className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-ink text-white flex items-center gap-0.5"
                    >
                      <Icon size={8} weight="fill" />
                      {ch.label}
                    </span>
                  );
                })}
          </div>

          {/* Created date + assigned person (bottom-right, clickable for date edit) */}
          <div className="flex flex-col items-end gap-0.5 shrink-0">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  onPointerDown={stopPointer}
                  data-testid={`note-date-${note.id}`}
                  className="text-[9px] text-ink/65 font-bold flex items-center gap-1 hover:text-ink transition-colors"
                >
                  <Calendar size={9} weight="bold" />
                  {formatDateTime(note.note_date || note.created_at)}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-auto p-2"
                onPointerDown={stopPointer}
              >
                <input
                  type="date"
                  value={isoToDateInput(note.note_date || note.created_at)}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const iso = new Date(e.target.value).toISOString();
                    onUpdateField && onUpdateField(note.id, { note_date: iso });
                    setDatePickerOpen(false);
                  }}
                  className="text-xs border border-black/10 rounded px-2 py-1"
                />
              </PopoverContent>
            </Popover>
            {note.assigned_to && (
              <span
                className="text-[9px] uppercase tracking-wider text-ink/75 font-bold"
                data-testid={`note-assigned-${note.id}`}
              >
                {note.assigned_to}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-1.5 text-xs">
      <span className="text-[9px] uppercase tracking-widest text-ink/50 font-bold shrink-0">
        {label}
      </span>
      <span className="text-ink/85 font-medium truncate font-arial">{value}</span>
    </div>
  );
}
